import { createContext, useContext } from 'react';

import { useSimpleLocalStorage } from '../../hooks/use-simple-local-storage';

import {
  TIMEZONE_LOCAL,
  TIMEZONE_LS_KEY,
  TIMEZONE_TO_MUI,
  AVAILABLE_TIMEZONES,
  type Timezone,
  type TimezoneMui,
} from './timezone.constants';

export interface TimezoneContextValue {
  /**
   * The current timezone
   * 'local' | 'utc'
   */
  timezone: Timezone;
  /**
   * The timezone value to pass to components
   * 'system' | 'UTC'
   */
  muiTimezone: TimezoneMui;
  /**
   * Available timezone options
   * 'local' | 'utc'
   */
  availableTimezones: Timezone[];
  setTimezone: (timezone: Timezone) => void;
}

const TimezoneContext = createContext<TimezoneContextValue | null>(null);

export const useTimezoneContext = (): TimezoneContextValue => {
  const value = useContext(TimezoneContext);

  if (value === null) {
    throw new Error(
      'Fenrir: useTimezoneContext must be used within the context of TimezoneProvider.',
    );
  }

  return value;
};

export interface TimezoneProviderProps {
  defaultTimezone?: Timezone;
}

export const TimezoneProvider: React.FC<
  React.PropsWithChildren<TimezoneProviderProps>
> = ({ defaultTimezone = TIMEZONE_LOCAL, children }) => {
  const [timezone, setTimezone] = useSimpleLocalStorage<Timezone>(
    TIMEZONE_LS_KEY,
    defaultTimezone,
  );

  const value = {
    timezone,
    muiTimezone: TIMEZONE_TO_MUI[timezone],
    availableTimezones: AVAILABLE_TIMEZONES,
    setTimezone,
  };

  return (
    <TimezoneContext.Provider value={value}>
      {children}
    </TimezoneContext.Provider>
  );
};
